const PSQ_MSG = {
  subscriptionSuccess: "You're subscribed! Check your inbox for exclusive offers.",
  subscriptionError: 'Subscription failed. Please try again later.',
  subscriptionEmailError: 'Please enter a valid email address.',
  subscriptionAlreadySubscribed: 'This email is already subscribed.',
  quizResultSuccess: "We've sent your full report to your email.",
  quizResultError: 'Subscription failed. Please try again later.',
};

const PSQ_RESTART_QUIZ_BUTTON_TEXT = 'Restart the quiz';

class DlPageSubscriptionQuizM extends HTMLElement {
  constructor() {
    super();
    this.onDocumentAnchorClick = this.onDocumentAnchorClick.bind(this);
    this.onHashChangeForTab = this.onHashChangeForTab.bind(this);
    this.onQuizKeydown = this.onQuizKeydown.bind(this);
    this.pendingAnchorTabIndex = null;
    this.activeTabIndex = null;
    this.suppressTabIndexAttrSync = false;
    this.tabsAttrObserver = null;
  }

  connectedCallback() {
    this.tabItems = this.querySelectorAll('.dl-page-subscription-quiz-m-tab-item');
    this.contentItems = this.querySelectorAll('.dl-page-subscription-quiz-m-content-item');
    this.tabsContainer = this.querySelector('.dl-page-subscription-quiz-m-tabs');

    this.indicator = document.createElement('div');
    this.indicator.className = 'dl-page-subscription-quiz-m-tab-indicator';
    this.tabsContainer.appendChild(this.indicator);

    this.tabItems.forEach((tab, index) => {
      tab.addEventListener('click', () => this.switchTab(index));
    });

    this.bindTabsAttributeObserver();
    this.switchTab(this.getInitialTabIndex(), { syncAttribute: true });
    this.bindAnchorTabNavigation();

    this.loadSectionConfig();
    this.initQuiz();
    this.initSubscription();
    // Shopify form submit removed; keep method for backwards safety but no-op on most pages.
    this.restorePostSubmitState();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentAnchorClick, true);
    window.removeEventListener('hashchange', this.onHashChangeForTab);
    document.removeEventListener('keydown', this.onQuizKeydown);
    this.tabsAttrObserver?.disconnect();
    this.tabsAttrObserver = null;
  }

  getTabIndexHost() {
    return this.tabsContainer;
  }

  bindTabsAttributeObserver() {
    if (!this.tabsContainer) return;

    this.tabsAttrObserver = new MutationObserver((mutations) => {
      if (this.suppressTabIndexAttrSync) return;
      if (!this.tabItems || !this.tabItems.length) return;

      const shouldSync = mutations.some(
        (mutation) =>
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'data-tab-index')
      );
      if (!shouldSync) return;

      const nextIndex = this.getInitialTabIndex();
      if (nextIndex === this.activeTabIndex) return;
      this.switchTab(nextIndex, { syncAttribute: false });
    });

    this.tabsAttrObserver.observe(this.tabsContainer, {
      attributes: true,
      attributeFilter: ['data-tab-index', 'data-default-tab-index'],
    });
  }

  parseTabIndex(raw) {
    if (raw === null || raw === '') return null;
    const index = Number.parseInt(raw, 10);
    if (Number.isNaN(index)) return null;
    return index;
  }

  getInitialTabIndex() {
    const host = this.getTabIndexHost();
    if (!host) return 0;

    const tabIndex = this.parseTabIndex(host.getAttribute('data-tab-index'));
    if (tabIndex != null) return this.normalizeTabIndex(tabIndex);

    const defaultTabIndex = this.parseTabIndex(host.getAttribute('data-default-tab-index'));
    if (defaultTabIndex != null) return this.normalizeTabIndex(defaultTabIndex);

    return 0;
  }

  normalizeTabIndex(index) {
    if (!this.tabItems || !this.tabItems.length) return 0;
    if (index < 0) return 0;
    if (index >= this.tabItems.length) return this.tabItems.length - 1;
    return index;
  }

  syncTabIndexAttribute(index) {
    const host = this.getTabIndexHost();
    if (!host) return;

    this.suppressTabIndexAttrSync = true;
    host.setAttribute('data-tab-index', String(index));
    this.suppressTabIndexAttrSync = false;
  }

  /**
   * Read tab index from the anchor that initiated navigation (data-tab-index on <a>).
   */
  parseTabIndexFromLink(link) {
    if (!link) return null;
    const raw = link.getAttribute('data-tab-index');
    if (raw === null || raw === '') return null;
    const index = Number.parseInt(raw, 10);
    return Number.isNaN(index) ? null : index;
  }

  getHashTargetId() {
    const hash = location.hash;
    if (!hash || hash.length < 2) return null;
    return decodeURIComponent(hash.slice(1));
  }

  /** True when hash points at an element inside this section (e.g. tabs anchor id). */
  isHashTargetInSection(targetId) {
    if (!targetId) return false;
    const target = document.getElementById(targetId);
    return Boolean(target && this.contains(target));
  }

  applyTabFromAnchorLink(link) {
    const tabIndex = this.parseTabIndexFromLink(link);
    if (tabIndex == null) return;
    this.switchTab(this.normalizeTabIndex(tabIndex));
  }

  onDocumentAnchorClick(event) {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href || href === '#') return;

    const targetId = decodeURIComponent(href.slice(1));
    if (!this.isHashTargetInSection(targetId)) return;

    this.pendingAnchorTabIndex = this.parseTabIndexFromLink(link);

    this.applyTabFromAnchorLink(link);
  }

  findAnchorLinkForTargetId(targetId) {
    if (!targetId) return null;
    try {
      return document.querySelector(`a[href="#${CSS.escape(targetId)}"]`);
    } catch {
      return document.querySelector(`a[href="#${targetId}"]`);
    }
  }

  applyTabFromHashIfInSection() {
    const targetId = this.getHashTargetId();
    if (!this.isHashTargetInSection(targetId)) return;
    this.applyTabFromAnchorLink(this.findAnchorLinkForTargetId(targetId));
  }

  onHashChangeForTab() {
    const targetId = this.getHashTargetId();
    if (!this.isHashTargetInSection(targetId)) return;

    if (typeof this.pendingAnchorTabIndex === 'number') {
      this.switchTab(this.pendingAnchorTabIndex);
      this.pendingAnchorTabIndex = null;
      return;
    }

    this.applyTabFromHashIfInSection();
  }

  bindAnchorTabNavigation() {
    document.addEventListener('click', this.onDocumentAnchorClick, true);
    window.addEventListener('hashchange', this.onHashChangeForTab);
    this.applyTabFromHashIfInSection();
  }

  switchTab(activeIndex, { syncAttribute = true } = {}) {
    const normalizedIndex = this.normalizeTabIndex(activeIndex);
    this.activeTabIndex = normalizedIndex;

    this.tabItems.forEach((tab, index) => {
      tab.classList.toggle('dl-active', index === normalizedIndex);
    });

    this.contentItems.forEach((content, index) => {
      content.hidden = index !== normalizedIndex;
    });

    const activeTab = this.tabItems[normalizedIndex];
    if (activeTab && this.indicator) {
      this.indicator.style.width = activeTab.offsetWidth + 'px';
      this.indicator.style.transform = `translateX(${activeTab.offsetLeft}px)`;
    }

    if (syncAttribute) {
      this.syncTabIndexAttribute(normalizedIndex);
    }
  }

  loadSectionConfig() {
    const configScript = this.querySelector('.dl-psq-quiz-config');
    try {
      this.sectionConfig = JSON.parse(configScript?.textContent || '{}');
    } catch (e) {
      this.sectionConfig = {};
    }
    this.events = this.sectionConfig.events || {};
  }

  initQuiz() {
    this.quizRoot = this.querySelector('.dl-psq-quiz');
    if (!this.quizRoot) return;

    this.quizCover = this.quizRoot.querySelector('.dl-psq-quiz-cover');
    this.quizCoverContent = this.quizRoot.querySelector('.dl-psq-quiz-cover-content');
    this.startBtn = this.quizRoot.querySelector('.dl-psq-quiz-start-btn');
    this.quizFlow = this.quizRoot.querySelector('.dl-psq-quiz-flow');
    this.quizPanel = this.quizRoot.querySelector('.dl-psq-quiz-panel');
    this.stageEl = this.quizRoot.querySelector('.dl-psq-quiz-stage');
    this.progressEl = this.quizRoot.querySelector('.dl-psq-quiz-progress');
    this.progressFill = this.quizRoot.querySelector('.dl-psq-quiz-progress-bar-fill');
    this.progressMeta = this.quizRoot.querySelector('.dl-psq-quiz-progress-meta');
    this.btnBack = this.quizRoot.querySelector('.dl-psq-quiz-btn-back');
    this.btnNext = this.quizRoot.querySelector('.dl-psq-quiz-btn-next');
    this.btnFinish = this.quizRoot.querySelector('.dl-psq-quiz-btn-finish');
    this.btnCancel = this.quizRoot.querySelector('.dl-psq-quiz-btn-cancel');

    this.quizConfig = this.sectionConfig || {};

    this.infoFields = Array.isArray(this.quizConfig.infoFields) ? this.quizConfig.infoFields : [];
    this.questions = Array.isArray(this.quizConfig.questions) ? this.quizConfig.questions : [];
    this.results = Array.isArray(this.quizConfig.results) ? this.quizConfig.results : [];
    this.mbtiEntries = Array.isArray(this.quizConfig.mbtiEntries) ? this.quizConfig.mbtiEntries : [];
    this.strings = this.quizConfig.strings || {};
    this.resultDefaultKey = this.quizConfig.resultDefaultKey || 'default';

    this.originalCoverBackgroundImage = this.quizCover?.style?.backgroundImage || '';

    // State
    this.currentStepIndex = 0; // 0 = info page, 1..n = questions, last = result
    this.infoAnswers = {};
    this.questionAnswers = {}; // { [questionId]: { optionIndex, label, resultKey } }
    this.finalResultKey = null;
    this.answerString = '';
    this.matchedMBTI = null;
    this.isSubscribing = false;
    this.messageTimers = new WeakMap();

    this.bindQuizEvents();
    this.renderStep();
    this.updateActions();
  }

  initSubscription() {
    this.subscriptionForm = this.querySelector('.dl-psq-subscription-form');
    this.subscriptionEmailInput = this.querySelector('.dl-psq-subscription-input');
    this.subscriptionButton = this.querySelector('.dl-psq-subscription-submit');
    this.isSubscriptionSubmitting = false;

    if (!this.subscriptionForm) return;

    // No Shopify submit: always prevent native submit.
    this.subscriptionForm.addEventListener(
      'submit',
      (e) => {
        e.preventDefault();
      },
      { capture: true }
    );

    if (this.subscriptionButton) {
      this.subscriptionButton.addEventListener('click', () => this.handleSubscriptionSubmit());
    }

    if (this.subscriptionEmailInput) {
      this.subscriptionEmailInput.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        this.handleSubscriptionSubmit();
      });
    }

    this.resetFormMessage(this.subscriptionForm);
  }

  resetFormMessage(form) {
    if (!form) return;
    const msgEl = form.querySelector('.dl-psq-subscription-message');
    if (!msgEl) return;
    msgEl.hidden = true;
    msgEl.textContent = '';
    msgEl.removeAttribute('data-state');
    msgEl.classList.remove('is-success', 'is-error');
  }

  showFormMessage(form, state, text) {
    if (!form) return;
    const msgEl = form.querySelector('.dl-psq-subscription-message');
    if (!msgEl) return;

    const prev = this.messageTimers?.get?.(form);
    if (prev) window.clearTimeout(prev);

    msgEl.hidden = false;
    msgEl.textContent = text || '';
    msgEl.dataset.state = state;
    msgEl.classList.toggle('is-success', state === 'success');
    msgEl.classList.toggle('is-error', state === 'error');

    const t = window.setTimeout(() => {
      this.resetFormMessage(form);
    }, 4000);
    this.messageTimers?.set?.(form, t);
  }

  /** Scroll quiz internal scroller (not window) to the given element. */
  scrollQuizInternalTo(el) {
    if (!el) return;
    const scroller = el.closest?.('.dl-psq-quiz-cover.is-flow') || this.quizCover;
    if (!scroller || !(scroller instanceof Element)) return;
    if (!scroller.classList.contains('is-flow')) return;

    window.setTimeout(() => {
      const scrollerRect = scroller.getBoundingClientRect();
      const rect = el.getBoundingClientRect();
      const top = rect.top - scrollerRect.top + scroller.scrollTop - 12;
      scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }, 0);
  }

  restorePostSubmitState() {
    // Shopify form submit removed: keep method for backwards compatibility only.
    const params = new URLSearchParams(window.location.search);
    const hasCustomerResult =
      params.has('customer_post') ||
      params.has('customer_posted_successfully') ||
      params.has('contact_posted_successfully') ||
      (params.get('form_type') === 'customer');
    if (!hasCustomerResult) return;

    let last = null;
    try {
      last = window.sessionStorage.getItem('dl_psq_last_submit');
    } catch (e) {}

    // If last submit was quiz result, restore quiz UI so the message rendered in template can be seen.
    if (last === 'quiz_result') {
      try {
        const raw = window.sessionStorage.getItem('dl_psq_quiz_state');
        const state = raw ? JSON.parse(raw) : null;
        if (state && typeof state === 'object') {
          this.answerString = state.answerString || '';
          this.finalResultKey = state.finalResultKey || this.finalResultKey;
          this.matchedMBTI = state.matchedMBTI || this.matchedMBTI;
          this.infoAnswers = state.infoAnswers || this.infoAnswers;
        }
      } catch (e) {}

      // Show quiz tab + result page
      this.switchTab(1);
      if (this.quizCoverContent) this.quizCoverContent.hidden = true;
      if (this.quizFlow) this.quizFlow.hidden = false;
      if (this.quizCover) {
        this.quizCover.classList.add('is-flow');
        this.quizCover.style.backgroundImage = '';
      }
      this.currentStepIndex = this.getResultStepIndex();
      this.renderStep();
      this.updateActions();
    }

    if (last === 'subscription') {
      this.switchTab(0);
      this.syncSubscriptionDisabledState();
    }

    // clear marker once consumed
    try {
      window.sessionStorage.removeItem('dl_psq_last_submit');
      // keep dl_psq_quiz_state for a bit; harmless
    } catch (e) {}
  }

  syncSubscriptionDisabledState() {}

  /** Email from Subscription tab (session), for prefill on quiz result subscribe. */
  static SUBSCRIBED_EMAIL_STORAGE_KEY = 'dl_psq_subscribed_email';

  /** Tab 1 successful subscriptions (sessionStorage, this browser session only). */
  static TAB_SUBSCRIPTION_EMAILS_KEY = 'dl_psq_tab_subscription_emails';

  normalizeSubscriptionEmail(email) {
    return (email || '').trim().toLowerCase();
  }

  getTabSubscriptionEmails() {
    try {
      const raw = window.sessionStorage.getItem(DlPageSubscriptionQuizM.TAB_SUBSCRIPTION_EMAILS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map((item) => this.normalizeSubscriptionEmail(item)).filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  isTabSubscriptionEmailRecorded(email) {
    const normalized = this.normalizeSubscriptionEmail(email);
    if (!normalized) return false;
    return this.getTabSubscriptionEmails().includes(normalized);
  }

  saveTabSubscriptionEmail(email) {
    const normalized = this.normalizeSubscriptionEmail(email);
    if (!normalized) return;
    const emails = new Set(this.getTabSubscriptionEmails());
    emails.add(normalized);
    try {
      window.sessionStorage.setItem(
        DlPageSubscriptionQuizM.TAB_SUBSCRIPTION_EMAILS_KEY,
        JSON.stringify([...emails])
      );
    } catch (e) {
      // ignore
    }
  }

  saveSubscribedEmail(email) {
    const value = (email || '').trim();
    if (!value) return;
    this.subscribedEmail = value;
    try {
      window.sessionStorage.setItem(DlPageSubscriptionQuizM.SUBSCRIBED_EMAIL_STORAGE_KEY, value);
    } catch (e) {
      // ignore
    }
  }

  getSubscribedEmail() {
    if (this.subscribedEmail) return this.subscribedEmail;
    try {
      const stored = window.sessionStorage.getItem(DlPageSubscriptionQuizM.SUBSCRIBED_EMAIL_STORAGE_KEY);
      if (stored) this.subscribedEmail = stored.trim();
    } catch (e) {
      // ignore
    }
    return this.subscribedEmail || '';
  }

  prefillResultSubscribeEmail() {
    const email = this.getSubscribedEmail();
    if (!email || !this.stageEl) return;
    const input = this.stageEl.querySelector(
      '.dl-psq-quiz-result-shopify-form input[name="contact[email]"]'
    );
    if (!input || (input.value || '').trim()) return;
    input.value = email;
  }

  handleSubscriptionSubmit() {
    if (this.isSubscriptionSubmitting) return;
    if (!this.subscriptionForm || !this.subscriptionEmailInput) return;

    const email = (this.subscriptionEmailInput.value || '').trim();
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      const errorText = PSQ_MSG.subscriptionEmailError;
      this.showFormMessage(this.subscriptionForm, 'error', errorText);
      return;
    }

    if (this.isTabSubscriptionEmailRecorded(email)) {
      const alreadyText =
        this.subscriptionForm.getAttribute('data-already-subscribed-text') ||
        PSQ_MSG.subscriptionAlreadySubscribed;
      this.showFormMessage(this.subscriptionForm, 'error', alreadyText);
      return;
    }

    this.isSubscriptionSubmitting = true;
    if (this.subscriptionButton) {
      this.subscriptionButton.disabled = true;
      this.subscriptionButton.classList.add('is-disabled');
      this.subscriptionButton.setAttribute('aria-disabled', 'true');
    }
    this.subscriptionEmailInput.disabled = true;

    try {
      // Braze + Amplitude (through DLPixelH)
      const payload = {
        source: 'subscription_tab',
        email,
      };
      if (typeof DLPixelH !== 'undefined') {
        if (typeof DLPixelH.identify === 'function') {
          DLPixelH.identify(email);
        }
        const submitEvent = this.events.submit || 'subscribe_submit';
        if (typeof DLPixelH.capture === 'function' && submitEvent) {
          DLPixelH.capture(submitEvent, payload);
        }
      }
      const ok = this.syncSubscribeToBraze(email) !== false;
      if (ok) {
        this.saveTabSubscriptionEmail(email);
        this.saveSubscribedEmail(email);
      }
      const successText =
        this.subscriptionForm.getAttribute('data-success-text') || PSQ_MSG.subscriptionSuccess;
      const errorText = this.subscriptionForm.getAttribute('data-error-text') || PSQ_MSG.subscriptionError;
      this.showFormMessage(this.subscriptionForm, ok ? 'success' : 'error', ok ? successText : errorText);
    } catch (e) {
      const errorText = this.subscriptionForm.getAttribute('data-error-text') || PSQ_MSG.subscriptionError;
      this.showFormMessage(this.subscriptionForm, 'error', errorText);
    }
    // Restore default state (message will auto-hide after 4s)
    if (this.subscriptionButton) {
      this.subscriptionButton.disabled = false;
      this.subscriptionButton.classList.remove('is-disabled');
      this.subscriptionButton.setAttribute('aria-disabled', 'false');
    }
    this.subscriptionEmailInput.disabled = false;
    this.isSubscriptionSubmitting = false;
  }

  bindQuizEvents() {
    if (this.startBtn) {
      this.startBtn.addEventListener('click', () => this.startQuiz());
    }
    if (this.btnBack) {
      this.btnBack.addEventListener('click', () => this.goBack());
    }
    if (this.btnNext) {
      this.btnNext.addEventListener('click', () => this.goNext());
    }
    if (this.btnFinish) {
      this.btnFinish.addEventListener('click', () => this.finishQuiz());
    }
    if (this.btnCancel) {
      this.btnCancel.addEventListener('click', () => this.cancelQuizToCover());
    }

    // Delegated events inside stage (options + info inputs)
    if (this.stageEl) {
      this.stageEl.addEventListener('click', (e) => {
        const optionBtn = e.target?.closest?.('[data-quiz-option]');
        if (optionBtn) {
          const qid = optionBtn.getAttribute('data-question-id');
          const optIndex = Number(optionBtn.getAttribute('data-option-index'));
          this.selectOption(qid, optIndex);
          return;
        }
      });

      this.stageEl.addEventListener('beforeinput', (e) => {
        const input = e.target?.closest?.('[data-info-key]');
        if (!input) return;
        this.handleInfoFieldBeforeInput(input, e);
      });

      this.stageEl.addEventListener('keydown', (e) => {
        const input = e.target?.closest?.('[data-info-key]');
        if (!input) return;
        this.handleInfoFieldKeydown(input, e);
      });

      this.stageEl.addEventListener('input', (e) => {
        const input = e.target?.closest?.('[data-info-key]');
        if (input) this.handleInfoFieldInput(input);
      });

      this.stageEl.addEventListener('paste', (e) => {
        const input = e.target?.closest?.('[data-info-key]');
        if (input) this.handleInfoFieldPaste(input, e);
      });
    }

    this.bindQuizKeyboard();
  }

  getInfoFieldByKey(key) {
    if (!key) return null;
    return (this.infoFields || []).find((f) => f?.key === key) || null;
  }

  getInfoFieldMaxLength(field) {
    const n = Number(field?.maxLength);
    if (!Number.isFinite(n) || n <= 0) return 10;
    return Math.floor(n);
  }

  getInfoFieldMaxLengthTip(field, maxLength) {
    const max = maxLength || this.getInfoFieldMaxLength(field);
    const raw =
      field?.maxLengthTip ||
      this.strings.infoMaxLengthTip ||
      'Pet name cannot exceed {max} characters.';
    return String(raw).replaceAll('{max}', String(max));
  }

  showInfoMaxLengthTip(input) {
    const key = input?.getAttribute?.('data-info-key');
    const field = this.getInfoFieldByKey(key) || { key, maxLength: 10 };
    const maxLength = this.getInfoFieldMaxLength(field);
    this.showInlineMessage(this.getInfoFieldMaxLengthTip(field, maxLength));
  }

  /** Predict value after beforeinput; used to tip when user would exceed max. */
  getInfoFieldNextValue(input, e) {
    const value = input.value || '';
    const start = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? value.length;
    const type = e.inputType || '';

    if (type.startsWith('delete') || type === 'historyUndo' || type === 'historyRedo') {
      return null;
    }

    let insert = '';
    if (typeof e.data === 'string') insert = e.data;
    else if (type === 'insertFromPaste') insert = ''; // handled in paste
    else if (type === 'insertLineBreak' || type === 'insertParagraph') insert = '\n';
    else return null;

    return value.slice(0, start) + insert + value.slice(end);
  }

  handleInfoFieldBeforeInput(input, e) {
    const key = input.getAttribute('data-info-key');
    const field = this.getInfoFieldByKey(key);
    const maxLength = this.getInfoFieldMaxLength(field);
    if (maxLength <= 0) return;

    // Paste is handled in paste handler (clipboard data more reliable)
    if (e.inputType === 'insertFromPaste') return;

    const next = this.getInfoFieldNextValue(input, e);
    if (next == null) return;

    if (next.length > maxLength) {
      this.showInfoMaxLengthTip(input);
      // Allow browser maxlength / our input trim to enforce; tip must still show
    }
  }

  handleInfoFieldKeydown(input, e) {
    // Fallback when beforeinput unavailable / maxlength silently blocks typing
    if (e.isComposing || e.key === 'Unidentified') return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key.length !== 1) return; // only printable chars

    const key = input.getAttribute('data-info-key');
    const field = this.getInfoFieldByKey(key);
    const maxLength = this.getInfoFieldMaxLength(field);
    if (maxLength <= 0) return;

    const value = input.value || '';
    const start = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? value.length;
    const selected = Math.max(0, end - start);
    if (value.length - selected >= maxLength) {
      this.showInfoMaxLengthTip(input);
    }
  }

  handleInfoFieldPaste(input, e) {
    const key = input.getAttribute('data-info-key');
    const field = this.getInfoFieldByKey(key);
    const maxLength = this.getInfoFieldMaxLength(field);
    if (maxLength <= 0) return;

    const pasteText = e.clipboardData?.getData?.('text') ?? '';
    if (!pasteText) return;

    const value = input.value || '';
    const start = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? value.length;
    const next = value.slice(0, start) + pasteText + value.slice(end);

    if (next.length > maxLength) {
      e.preventDefault();
      const allowed = Math.max(0, maxLength - (value.length - (end - start)));
      const inserted = pasteText.slice(0, allowed);
      input.value = value.slice(0, start) + inserted + value.slice(end);
      const caret = start + inserted.length;
      try {
        input.setSelectionRange(caret, caret);
      } catch (_) {
        /* ignore */
      }
      this.infoAnswers[key] = input.value;
      this.showInfoMaxLengthTip(input);
    }
  }

  handleInfoFieldInput(input) {
    const key = input.getAttribute('data-info-key');
    const field = this.getInfoFieldByKey(key);
    const maxLength = this.getInfoFieldMaxLength(field);

    if (maxLength > 0 && input.value.length > maxLength) {
      input.value = input.value.slice(0, maxLength);
      this.showInfoMaxLengthTip(input);
    } else if (maxLength > 0 && input.value.length < maxLength) {
      // Clear tip only when user is back under the limit
      const el = this.stageEl?.querySelector?.('.dl-psq-quiz-inline-message');
      if (el) {
        const tip = this.getInfoFieldMaxLengthTip(field, maxLength);
        if (el.textContent === tip) this.clearInlineMessage();
      }
    }

    this.infoAnswers[key] = input.value;
  }

  bindQuizKeyboard() {
    if (!this.quizRoot) return;
    document.addEventListener('keydown', this.onQuizKeydown);
  }

  isQuizTabActive() {
    const quizPane = this.querySelector('.dl-page-subscription-quiz-m-content-item[dl-type="quiz"]');
    return Boolean(quizPane && quizPane.style.display !== 'none');
  }

  isQuizActionButtonVisible(btn) {
    return Boolean(btn && !btn.hidden);
  }

  onQuizKeydown(e) {
    if (!this.quizRoot || !this.isQuizTabActive()) return;
    if (e.target?.closest?.('.dl-psq-quiz-result-shopify-form')) return;

    if (e.key === 'Enter') {
      this.handleQuizEnterKey(e);
    } else if (e.key === 'Escape') {
      this.handleQuizEscapeKey(e);
    } else if (e.key === 'Backspace') {
      // Backspace should behave like "back" only when NOT editing text.
      const t = e.target;
      const isEditable =
        (t instanceof HTMLTextAreaElement) ||
        (t instanceof HTMLInputElement &&
          !['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'color', 'range'].includes(
            (t.type || '').toLowerCase()
          )) ||
        Boolean(t?.isContentEditable);

      if (isEditable) return;

      e.preventDefault(); // avoid browser history back
      this.handleQuizEscapeKey(e);
    }
  }

  handleQuizEnterKey(e) {
    if (e.target?.closest?.('.dl-psq-quiz-actions .dl-psq-quiz-btn')) return;

    if (this.quizFlow?.hidden) {
      if (this.startBtn && !this.quizCoverContent?.hidden) {
        e.preventDefault();
        this.startQuiz();
      }
      return;
    }

    if (this.currentStepIndex === this.getResultStepIndex()) return;

    if (this.isQuizActionButtonVisible(this.btnFinish)) {
      e.preventDefault();
      this.finishQuiz();
      return;
    }

    if (this.isQuizActionButtonVisible(this.btnNext)) {
      e.preventDefault();
      this.goNext();
    }
  }

  handleQuizEscapeKey(e) {
    if (this.quizFlow?.hidden) return;
    if (this.currentStepIndex === this.getResultStepIndex()) return;

    if (this.isQuizActionButtonVisible(this.btnCancel)) {
      e.preventDefault();
      this.cancelQuizToCover();
      return;
    }

    if (this.isQuizActionButtonVisible(this.btnBack)) {
      e.preventDefault();
      this.goBack();
    }
  }

  startQuiz() {
    if (this.quizCoverContent) this.quizCoverContent.hidden = true;
    if (this.quizFlow) this.quizFlow.hidden = false;
    if (this.quizCover) {
      this.quizCover.classList.add('is-flow');
      // Clear inline bg so section CSS (quiz_flow_image) can apply
      this.quizCover.style.backgroundImage = '';
    }
    this.currentStepIndex = 0;
    this.renderStep();
    this.updateActions();
    if (this.events.start) window.DLPixelH?.capture?.(this.events.start, { step: 'start' });
  }

  /** 信息第一步：取消并回到封面 Start Quiz */
  cancelQuizToCover() {
    if (this.currentStepIndex !== 0) return;

    this.clearInlineMessage();

    if (this.quizFlow) this.quizFlow.hidden = true;
    if (this.quizCoverContent) this.quizCoverContent.hidden = false;

    if (this.quizCover) {
      this.quizCover.classList.remove('is-flow');
      this.quizCover.style.backgroundImage = '';
    }

    this.updateActions();
  }

  goBack() {
    if (this.currentStepIndex <= 0) return;
    clearTimeout(this._optionAdvanceTimer);
    this.currentStepIndex -= 1;
    this.renderStep();
    this.updateActions();
  }

  goNext() {
    if (!this.validateCurrentStep()) return;
    if (this.currentStepIndex >= this.getLastQuestionStepIndex()) return;
    this.currentStepIndex += 1;
    this.renderStep();
    this.updateActions();
  }

  finishQuiz() {
    if (!this.validateCurrentStep()) return;
    // Compute results, then show subscribe gate first
    this.answerString = this.buildAnswerString();
    this.matchedMBTI = this.matchMBTIEntry(this.answerString);
    this.finalResultKey = this.matchedMBTI?.mbti_type || this.computeResultKey();
    this.currentStepIndex = this.getSubscribeStepIndex();
    this.renderStep();
    this.updateActions();

    if (this.events.finish) {
      window.DLPixelH?.capture?.(this.events.finish, this.buildTrackingPayload({ stage: 'finish' }));
    }
  }

  showFullResult() {
    this.currentStepIndex = this.getResultStepIndex();
    this.renderStep();
    this.updateActions();
  }

  getLastQuestionStepIndex() {
    // If there are N questions, their step indices are 1..N
    return this.questions.length;
  }

  getSubscribeStepIndex() {
    // Subscribe gate: N+1 (after info + all questions)
    return this.questions.length + 1;
  }

  getResultStepIndex() {
    // Actual result card: N+2 (after subscribe gate)
    return this.questions.length + 2;
  }

  validateCurrentStep() {
    const requiredTip = this.strings.requiredTip || 'Please complete this question to continue.';

    // Info step
    if (this.currentStepIndex === 0) {
      const fields = this.infoFields.length
        ? this.infoFields
        : [{ key: 'pet_name', required: true, maxLength: 10 }];
      for (const field of fields) {
        const val = (this.infoAnswers[field.key] || '').trim();
        if (field?.required && !val) {
          this.showInlineMessage(requiredTip);
          return false;
        }
        const maxLength = this.getInfoFieldMaxLength(field);
        if (maxLength > 0 && (this.infoAnswers[field.key] || '').length > maxLength) {
          this.showInlineMessage(this.getInfoFieldMaxLengthTip(field, maxLength));
          return false;
        }
      }
      this.clearInlineMessage();
      return true;
    }

    // Question steps
    if (this.currentStepIndex >= 1 && this.currentStepIndex <= this.questions.length) {
      const q = this.questions[this.currentStepIndex - 1];
      const ans = q ? this.questionAnswers[q.id] : null;
      if (!ans) {
        this.showInlineMessage(requiredTip);
        return false;
      }
      this.clearInlineMessage();
      return true;
    }

    return true;
  }

  showInlineMessage(message) {
    if (!this.stageEl) return;
    let el = this.stageEl.querySelector('.dl-psq-quiz-inline-message');
    if (!el) {
      el = document.createElement('div');
      el.className = 'dl-psq-quiz-inline-message';
      // Prefer under the email input on subscribe gate, else under info fields
      const emailRow = this.stageEl.querySelector('.dl-psq-quiz-subscribe-gate-form .dl-psq-subscription-row');
      const infoFields = this.stageEl.querySelector('.dl-psq-quiz-info-fields');
      const anchor = emailRow || infoFields;
      if (anchor?.parentNode) {
        anchor.parentNode.insertBefore(el, anchor.nextSibling);
      } else {
        this.stageEl.appendChild(el);
      }
    }
    el.hidden = false;
    el.textContent = message;
  }

  clearInlineMessage() {
    if (!this.stageEl) return;
    const el = this.stageEl.querySelector('.dl-psq-quiz-inline-message');
    if (el) el.remove();
  }

  selectOption(questionId, optionIndex) {
    const q = this.questions.find((x) => x.id === questionId);
    if (!q) return;
    const opt = q.options?.[optionIndex];
    if (!opt) return;

    this.questionAnswers[questionId] = {
      optionIndex,
      label: opt.label,
      resultKey: opt.resultKey,
    };

    // Update UI highlight
    const all = this.stageEl?.querySelectorAll?.(`[data-question-id="${CSS.escape(questionId)}"][data-quiz-option]`) || [];
    all.forEach((btn) => btn.classList.remove('is-selected'));
    const current = this.stageEl?.querySelector?.(
      `[data-question-id="${CSS.escape(questionId)}"][data-option-index="${optionIndex}"]`
    );
    if (current) current.classList.add('is-selected');

    this.clearInlineMessage();
    if (this.events.answer) {
      window.DLPixelH?.capture?.(
        this.events.answer,
        this.buildTrackingPayload({ stage: 'answer', questionId, optionIndex })
      );
    }

    // Clicking an option confirms the answer — no Next/Finish needed
    const isLastQuestion = this.currentStepIndex === this.questions.length;
    clearTimeout(this._optionAdvanceTimer);
    this._optionAdvanceTimer = setTimeout(() => {
      if (isLastQuestion) this.finishQuiz();
      else this.goNext();
    }, 180);
  }

  computeResultKey() {
    const tally = new Map();
    for (const q of this.questions) {
      const ans = this.questionAnswers[q.id];
      const key = ans?.resultKey;
      if (!key) continue;
      tally.set(key, (tally.get(key) || 0) + 1);
    }

    let bestKey = null;
    let bestCount = -1;
    for (const [key, count] of tally.entries()) {
      if (count > bestCount) {
        bestCount = count;
        bestKey = key;
      }
    }

    if (bestKey) return bestKey;
    if (this.results.find((r) => r.key === this.resultDefaultKey)) return this.resultDefaultKey;
    return this.results[0]?.key || this.resultDefaultKey;
  }

  buildAnswerString() {
    // Build like "1234..." based on question order (1-based option index)
    return this.questions
      .map((q) => {
        const ans = this.questionAnswers[q.id];
        if (!ans || typeof ans.optionIndex !== 'number') return '';
        return String(ans.optionIndex + 1);
      })
      .join('');
  }

  /** 1-based：答到第几题（含当前所在题；结果页为总题数） */
  getQuizQuestionReached() {
    const total = Array.isArray(this.questions) ? this.questions.length : 0;
    if (!total) return 0;

    let reached = 0;
    this.questions.forEach((q, idx) => {
      const ans = this.questionAnswers[q.id];
      if (ans && typeof ans.optionIndex === 'number') {
        reached = Math.max(reached, idx + 1);
      }
    });

    if (this.currentStepIndex >= 1 && this.currentStepIndex <= total) {
      reached = Math.max(reached, this.currentStepIndex);
    }
    if (this.currentStepIndex === this.getResultStepIndex()) {
      reached = total;
    }

    return reached;
  }

  scorePatternSpecificity(pattern) {
    // Higher score = more specific
    const p = String(pattern || '');
    let score = 0;
    for (let i = 0; i < p.length; i++) {
      const ch = p[i];
      if (ch >= '0' && ch <= '9') score += 2;
      if (ch === '[') score += 1;
      if (ch === '*') score -= 1;
    }
    // Treat "**" as more generic
    if (p.includes('**')) score -= 3;
    return score;
  }

  patternToRegex(pattern) {
    // Syntax:
    // - digits: exact match
    // - * : any single digit
    // - ** : any sequence (including empty)
    // - [124] : any of 1/2/4 for this position
    const p = String(pattern || '').trim();
    let out = '^';

    for (let i = 0; i < p.length; i++) {
      const ch = p[i];
      if (ch === '*') {
        if (p[i + 1] === '*') {
          out += '[0-9]*';
          i += 1;
        } else {
          out += '[0-9]';
        }
        continue;
      }

      if (ch === '[') {
        const end = p.indexOf(']', i + 1);
        if (end !== -1) {
          const inside = p.slice(i + 1, end).replace(/[^0-9]/g, '');
          out += inside ? `[${inside}]` : '[0-9]';
          i = end;
          continue;
        }
        // malformed, treat as any single digit
        out += '[0-9]';
        continue;
      }

      if (ch >= '0' && ch <= '9') {
        out += ch;
        continue;
      }

      // ignore other chars/spaces
    }

    out += '$';
    return new RegExp(out);
  }

  matchMBTIEntry(answerString) {
    if (!answerString) return null;
    const entries = Array.isArray(this.mbtiEntries) ? this.mbtiEntries : [];
    if (!entries.length) return null;

    // Prefer more specific patterns first
    const sorted = [...entries].sort((a, b) => {
      return this.scorePatternSpecificity(b?.answer_pattern) - this.scorePatternSpecificity(a?.answer_pattern);
    });

    for (const entry of sorted) {
      const pattern = entry?.answer_pattern;
      if (!pattern) continue;
      try {
        const regex = this.patternToRegex(pattern);
        if (regex.test(answerString)) {
          return {
            answer_pattern: pattern,
            mbti_type: entry?.mbti_type || '',
            mbti_role: entry?.mbti_role || '',
            mbti_description: entry?.mbti_description || '',
            mbti_suggestion: entry?.mbti_suggestion || '',
            mbti_image: entry?.mbti_image || '',
            mbti_image_m: entry?.mbti_image_m || '',
          };
        }
      } catch (e) {
        // ignore pattern errors
      }
    }

    return null;
  }

  getResultContent() {
    // Base template from section result blocks
    const key = this.finalResultKey || this.computeResultKey();
    const base =
      this.results.find((r) => r.key === key) ||
      this.results.find((r) => r.key === this.resultDefaultKey) ||
      this.results[0] ||
      null;

    // If a metaobject match exists, override display fields
    if (this.matchedMBTI) {
      const mbtiPc =
        this.matchedMBTI.mbti_image || base?.image || this.matchedMBTI.mbti_image_m || '';
      const mbtiM = this.matchedMBTI.mbti_image_m || this.matchedMBTI.mbti_image || base?.image || '';
      return {
        ...(base || {}),
        key: this.matchedMBTI.mbti_type || (base?.key || ''),
        subtitle: this.matchedMBTI.mbti_role || base?.subtitle || '',
        description: this.matchedMBTI.mbti_description || base?.description || '',
        suggestion:this.matchedMBTI.mbti_suggestion || base?.suggestion || '',
        image: mbtiPc || base?.image || '',
        image_m: mbtiM || base?.image || '',
        __matched: this.matchedMBTI,
      };
    }

    if (!base) return null;
    const fallback = base.image || '';
    return { ...base, image_m: base.image_m || fallback };
  }

  buildTrackingPayload(extra = {}) {
    const payload = {
      section_id: this.quizRoot?.dataset?.sectionId || '',
      url: window.location.href,
      info: { ...this.infoAnswers },
      pet_name: this.infoAnswers?.pet_name || null,
      answer_string: this.answerString || this.buildAnswerString(),
      answer_question_reached: this.getQuizQuestionReached(),
      matched_answer_pattern: this.matchedMBTI?.answer_pattern || null,
      matched_mbti_type: this.matchedMBTI?.mbti_type || null,
      answers: this.questions.map((q, idx) => {
        const ans = this.questionAnswers[q.id];
        return {
          index: idx + 1,
          question_id: q.id,
          title: q.title,
          option_index: ans?.optionIndex ?? null,
          option_label: ans?.label ?? null,
          result_key: ans?.resultKey ?? null,
        };
      }),
      result_key: this.finalResultKey || null,
      ...extra,
    };
    return payload;
  }

  renderStep() {
    if (!this.stageEl) return;

    // If we're still on cover (before start), do nothing.
    if (this.quizFlow?.hidden) return;

    // Info page
    if (this.currentStepIndex === 0) {
      const total = this.getSubscribeStepIndex();
      this.stageEl.innerHTML = this.renderInfoStepHTML();
      this.renderProgressMetaInCard(1, total);
      this.renderProgress(1, total);
      this.hydrateInfoValues();
      return;
    }

    // Question pages
    if (this.currentStepIndex >= 1 && this.currentStepIndex <= this.questions.length) {
      const q = this.questions[this.currentStepIndex - 1];
      const total = this.getSubscribeStepIndex();
      // We count info as Question 1, so question #1 becomes 2
      const humanIndex = this.currentStepIndex + 1;
      this.stageEl.innerHTML = this.renderQuestionHTML(q);
      this.renderProgressMetaInCard(humanIndex, total);
      this.renderProgress(humanIndex, total);
      this.hydrateQuestionSelection(q);
      return;
    }

    // Subscribe gate page
    if (this.currentStepIndex === this.getSubscribeStepIndex()) {
      const total = this.getSubscribeStepIndex();
      this.stageEl.innerHTML = this.renderSubscribeGateHTML();
      this.renderProgressMetaInCard(null, total, true);
      this.renderProgress(total - 1, total);
      this.mountSubscribeGate();
      return;
    }

    // Result page
    if (this.currentStepIndex === this.getResultStepIndex()) {
      const total = this.getSubscribeStepIndex();
      this.stageEl.innerHTML = this.renderResultHTML();
      this.renderProgressMetaInCard(null, total, true);
      this.renderProgress(total, total);
      this.mountResultShareFromTemplate();
      this.bindResultRestart();
      this.fitOutlinedSvgTexts();
      requestAnimationFrame(() => this.fitOutlinedSvgTexts());
      return;
    }
  }

  renderProgressMetaInCard(current, total, isResult = false) {
    if (!this.stageEl) return;
    const el = this.stageEl.querySelector('.dl-psq-quiz-progress-meta--card');
    if (!el) return;
    if (isResult) {
      el.textContent = 'Result';
      return;
    }
    const cur = Number(current) || 0;
    const tot = Number(total) || 0;
    el.innerHTML = `<span class="dl-psq-quiz-progress-current">${cur}</span>/${tot}`;
  }

  buildSocialShareUrl(platform, pageUrl) {
    const u = encodeURIComponent(pageUrl || '');
    switch (platform) {
      case 'facebook':
        return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
      case 'whatsapp':
        return `https://api.whatsapp.com/send?text=${u}`;
      case 'reddit':
        return `https://www.reddit.com/submit?url=${u}`;
      case 'x':
        return `https://twitter.com/intent/tweet?url=${u}`;
      default:
        return pageUrl || '#';
    }
  }

  mountResultShareFromTemplate() {
    const slot = this.stageEl?.querySelector?.('.dl-psq-quiz-result-share-slot');
    const tpl = this.quizRoot?.querySelector?.('template.dl-psq-quiz-result-share-template');
    if (!slot || !tpl?.content) return;
    slot.replaceWith(tpl.content.cloneNode(true));
    const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
    if (!pageUrl) return;
    const links = this.stageEl?.querySelectorAll?.('.dl-psq-quiz-result-share-link[data-share]') || [];
    links.forEach((a) => {
      const platform = a.getAttribute('data-share');
      if (!platform) return;
      const href = this.buildSocialShareUrl(platform, pageUrl);
      if (href && href !== '#') a.setAttribute('href', href);
    });
  }

  renderSubscribeGateHTML() {
    const title = this.strings.subscribeGateTitle || "Enter your email to get your pet's vibe results";
    const desc = this.strings.subscribeGateDesc || "You'll receive your pet's full results, plus an exclusive 15% discount.";
    const btnText = this.strings.subscribeButtonText || 'Get Full Results';
    const placeholder = this.strings.emailPlaceholder || 'Email';

    return `
      <div class="dl-psq-quiz-card dl-psq-quiz-card-subscribe-gate">
        <div class="dl-psq-quiz-card-inner">
          <div class="dl-psq-quiz-subscribe-gate-title">${this.escapeHTML(title)}</div>
          <div class="dl-psq-quiz-subscribe-gate-desc">${this.escapeHTML(desc)}</div>
          <form class="dl-psq-quiz-subscribe-gate-form" novalidate="novalidate">
            <div class="dl-psq-subscription-form-main">
              <div class="dl-psq-subscription-row">
                <input
                  class="dl-psq-subscription-input"
                  type="email"
                  name="contact[email]"
                  autocomplete="email"
                  required
                  placeholder="${this.escapeAttr(placeholder)}"
                >
              </div>
              <div class="dl-psq-subscription-agreement">
                <span class="dl-psq-subscription-agreement-text">
                  <p>By subscribing, you agree to receive marketing emails from Petlibro. You can unsubscribe anytime using the link in any email.<br/><a href="/policies/privacy-policy" target="_blank" title="Privacy Policy">Privacy Policy</a>  <a href="/policies/terms-of-service" target="_blank" title="Terms of Service">Terms of Service</a></p>
                </span>
              </div>
              <p class="dl-psq-subscription-message" aria-live="polite" hidden></p>
            </div>
            <button class="dl-psq-quiz-start-btn dl-psq-quiz-subscribe-gate-btn" type="button">
              ${this.escapeHTML(btnText)}
            </button>
          </form>
        </div>
      </div>
    `;
  }

  mountSubscribeGate() {
    if (!this.stageEl) return;
    const form = this.stageEl.querySelector('.dl-psq-quiz-subscribe-gate-form');
    if (!form) return;

    const btn = form.querySelector('.dl-psq-quiz-subscribe-gate-btn');
    const emailInput = form.querySelector('input[name="contact[email]"]');

    // Prefill if available
    const saved = this.getSubscribedEmail();
    if (saved && emailInput && !emailInput.value) emailInput.value = saved;

    const onSubmit = () => {
      const email = (emailInput?.value || '').trim();
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(email)) {
        this.showInlineMessage(this.strings.invalidEmailTip || 'Please enter a valid email address.');
        return;
      }

      this.clearInlineMessage();

      // Subscribe
      this.syncSubscribeToBraze(email);
      this.saveSubscribedEmail(email);

      const payload = this.buildTrackingPayload({ stage: 'subscribe', email, result_key: this.finalResultKey });
      if (this.events.subscribe) window.DLPixelH?.capture?.(this.events.subscribe, payload);

      // Advance to full result
      this.showFullResult();
    };

    if (btn) btn.addEventListener('click', onSubmit);
    if (emailInput) {
      emailInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); onSubmit(); }
      });
    }
    form.addEventListener('submit', (e) => { e.preventDefault(); onSubmit(); });
  }

  mountResultSubscribeFromTemplate() {
    const slot = this.stageEl?.querySelector?.('.dl-psq-quiz-result-subscribe-slot');
    const tpl = this.quizRoot?.querySelector?.('template.dl-psq-quiz-result-subscribe-template');
    if (!slot || !tpl?.content) return;
    slot.replaceWith(tpl.content.cloneNode(true));
    this.bindResultSubscribe();
    this.prefillResultSubscribeEmail();
  }

  bindResultSubscribe() {
    if (!this.stageEl) return;
    const form = this.stageEl.querySelector('.dl-psq-quiz-result-shopify-form');
    if (!form || form.dataset.psqSubscribeBound === '1') return;

    const button = form.querySelector('.dl-psq-quiz-result-subscribe-btn');
    const emailInput = form.querySelector('input[name="contact[email]"]');

    // No Shopify submit: always prevent native submit
    form.addEventListener(
      'submit',
      (e) => {
        e.preventDefault();
      },
      { capture: true }
    );

    const onFormAction = () => this.handleResultFormAction(form);

    if (button) {
      button.addEventListener('click', onFormAction);
    }

    if (emailInput) {
      emailInput.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        onFormAction();
      });
    }

    form.dataset.psqSubscribeBound = '1';
    this.resetFormMessage(form);
  }

  handleResultFormAction(form) {
    if (!form) return;
    if (form.dataset.psqMode === 'restart') {
      this.restartQuiz();
      return;
    }
    this.handleResultSubscribe(form);
  }

  setResultSubscribeRestartUI(form) {
    const button = form.querySelector('.dl-psq-quiz-result-subscribe-btn');
    const emailInput = form.querySelector('input[name="contact[email]"]');
    if (button) {
      if (!button.dataset.subscribeText) {
        button.dataset.subscribeText = (button.textContent || '').trim();
      }
      button.textContent = this.strings.restartQuizButtonText || PSQ_RESTART_QUIZ_BUTTON_TEXT;
      button.classList.add('is-restart-quiz');
      button.disabled = false;
      button.classList.remove('is-disabled');
      button.setAttribute('aria-disabled', 'false');
    }
    if (emailInput) emailInput.disabled = false;
    form.dataset.psqMode = 'restart';
  }

  bindResultRestart() {
    const btn = this.stageEl?.querySelector?.('.dl-psq-quiz-restart-btn');
    if (btn) btn.addEventListener('click', () => this.restartQuiz());
  }

  restartQuiz() {
    const email = (this.getSubscribedEmail() || '').trim();
    if (email) this.saveSubscribedEmail(email);

    if (this.events.restart) {
      window.DLPixelH?.capture?.(
        this.events.restart,
        this.buildTrackingPayload({ stage: 'restart', email: email || null })
      );
    }

    this.questionAnswers = {};
    this.finalResultKey = null;
    this.answerString = '';
    this.matchedMBTI = null;
    this.currentStepIndex = 0;
    this.clearInlineMessage();
    this.renderStep();
    this.updateActions();
  }

  renderProgress(current, total) {
    const pct = total > 0 ? Math.max(0, Math.min(100, (current / total) * 100)) : 0;
    const fills = this.quizRoot?.querySelectorAll?.('.dl-psq-quiz-progress-bar-fill') || [];
    fills.forEach((fill) => {
      fill.style.width = `${pct}%`;
    });
    if (this.progressMeta) this.progressMeta.textContent = '';
  }

  renderInfoStepHTML() {
    const fields = this.infoFields.length
      ? this.infoFields
      : [
          {
            key: 'pet_name',
            label: "What’s Your Pet’s Name ?",
            placeholder: "Enter your pet's name",
            required: true,
            maxLength: 10,
            maxLengthTip: 'Pet name cannot exceed {max} characters.',
          },
        ];

    const firstLabel = fields[0]?.label || '';
    const inputsHTML = fields
      .map((f) => {
        const maxLength = this.getInfoFieldMaxLength(f);
        return `
          <div class="dl-psq-quiz-info-field">
            <input
              class="dl-psq-quiz-info-input"
              type="text"
              data-info-key="${this.escapeAttr(f.key || '')}"
              placeholder="${this.escapeAttr(f.placeholder || '')}"
              maxlength="${maxLength}"
              ${f.required ? 'required' : ''}
            />
          </div>
        `;
      })
      .join('');

    return `
      <div class="dl-psq-quiz-card">
        <div class="dl-psq-quiz-card-inner">
          <div class="dl-psq-quiz-card-header">
            <div class="dl-psq-quiz-question-title">${this.escapeHTML(firstLabel)}</div>
            <div class="dl-psq-quiz-progress-meta dl-psq-quiz-progress-meta--card"></div>
          </div>
          <div class="dl-psq-quiz-progress-bar dl-psq-quiz-progress-bar--card" aria-hidden="true">
            <div class="dl-psq-quiz-progress-bar-fill"></div>
          </div>
          <div class="dl-psq-quiz-info-fields">${inputsHTML}</div>
        </div>
      </div>
    `;
  }

  hydrateInfoValues() {
    const inputs = this.stageEl?.querySelectorAll?.('[data-info-key]') || [];
    inputs.forEach((input) => {
      const key = input.getAttribute('data-info-key');
      if (!key) return;
      const val = this.infoAnswers[key];
      if (typeof val === 'string') input.value = val;
    });
  }

  renderQuestionHTML(q) {
    if (!q) return '';
    const options = Array.isArray(q.options) ? q.options : [];
    const optionsHTML = options
      .map((opt, idx) => {
        const label = opt?.label || '';
        return `
          <button
            type="button"
            class="dl-psq-quiz-option"
            data-quiz-option="1"
            data-question-id="${this.escapeAttr(q.id)}"
            data-option-index="${idx}"
          >
            ${this.escapeHTML(label)}
          </button>
        `;
      })
      .join('');

    return `
      <div class="dl-psq-quiz-card">
        <div class="dl-psq-quiz-card-inner">
          <div class="dl-psq-quiz-card-header">
            <div class="dl-psq-quiz-question-title">${this.escapeHTML(q.title || '')}</div>
            <div class="dl-psq-quiz-progress-meta dl-psq-quiz-progress-meta--card"></div>
          </div>
          <div class="dl-psq-quiz-progress-bar dl-psq-quiz-progress-bar--card" aria-hidden="true">
            <div class="dl-psq-quiz-progress-bar-fill"></div>
          </div>
          <div class="dl-psq-quiz-options">${optionsHTML}</div>
        </div>
      </div>
    `;
  }

  hydrateQuestionSelection(q) {
    if (!q) return;
    const ans = this.questionAnswers[q.id];
    if (!ans) return;
    const current = this.stageEl?.querySelector?.(
      `[data-question-id="${CSS.escape(q.id)}"][data-option-index="${ans.optionIndex}"]`
    );
    if (current) current.classList.add('is-selected');
  }

  /** Result hero: ≤990px M 图，≥991px PC 图（两 URL 相同时只输出单 img） */
  renderResultImageHTML(r) {
    const pc = (r?.image || '').trim();
    const m = (r?.image_m || r?.image || '').trim();
    const show = pc || m;
    if (!show) {
      return `<div class="dl-psq-quiz-result-image-wrap dl-psq-quiz-result-image-placeholder"></div>`;
    }
    const esc = (u) => this.escapeAttr(u);
    const src = pc || m;
    if (!pc || !m || pc === m) {
      return `<div class="dl-psq-quiz-result-image-wrap"><img class="dl-psq-quiz-result-image" src="${esc(
        src
      )}" alt="" loading="lazy"></div>`;
    }
    return `<div class="dl-psq-quiz-result-image-wrap"><picture>
      <source media="(max-width: 990px)" srcset="${esc(m)}">
      <img class="dl-psq-quiz-result-image" src="${esc(pc)}" alt="" loading="lazy">
    </picture></div>`;
  }

  renderResultHTML() {
    const r = this.getResultContent();
    const hint = this.strings.resultHint || '';
    const forYouBothTip = this.strings.forYouBothTip || 'For you both';

    const imgHTML = this.renderResultImageHTML(r);
    const petName = (this.infoAnswers?.pet_name || '').trim();

    const mbtiType = this.matchedMBTI?.mbti_type || this.finalResultKey || '';
    const restartText = this.strings.restartQuizButtonText || 'Restart';
    const nameValue = petName || '—';
    const roleValue = this.matchedMBTI?.mbti_role || r?.subtitle || '—';

    return `
      <div class="dl-psq-quiz-card dl-psq-quiz-card-result">
        <div class="dl-psq-quiz-card-inner dl-psq-quiz-result-layout">
          <div class="dl-psq-quiz-result-left">
            ${imgHTML}
          </div>
          <div class="dl-psq-quiz-result-right">
            <div class="dl-psq-quiz-result-fields">
              <div class="dl-psq-quiz-result-field dl-psq-quiz-result-field--name">
                <span class="dl-psq-quiz-result-label">Name</span>
                ${this.renderOutlinedSvgText(nameValue)}
              </div>
              <div class="dl-psq-quiz-result-field dl-psq-quiz-result-field--type">
                <span class="dl-psq-quiz-result-label">Pet type</span>
                ${this.renderOutlinedSvgText(roleValue, 'dl-psq-quiz-result-type-title')}
              </div>
            </div>
            <div class="dl-psq-quiz-result-type-note">
              <p>${this.escapeHTML(this.strings.resultHint || "Keep an eye on your inbox, we'll send your pet's vibe results and 15% offer to your email.")}</p>
            </div>
            <div class="dl-psq-quiz-result-actions">
              <button type="button" class="dl-psq-quiz-start-btn dl-psq-quiz-restart-btn">${this.escapeHTML(restartText)}</button>
              <div class="dl-psq-quiz-result-share-slot"></div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderOutlinedSvgText(text, extraClass = '') {
    const safe = this.escapeHTML(text || '');
    const label = this.escapeAttr(text || '');
    const cls = ['dl-psq-quiz-result-value', extraClass].filter(Boolean).join(' ');
    return `
      <svg class="${cls}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${label}">
        <text class="dl-psq-quiz-result-value-text" x="0" y="50%" dominant-baseline="central">${safe}</text>
      </svg>
    `;
  }

  fitOutlinedSvgTexts() {
    const svgs = [...(this.stageEl?.querySelectorAll?.('svg.dl-psq-quiz-result-value') || [])];
    if (!svgs.length) return;

    const fieldsRow = this.stageEl?.querySelector?.('.dl-psq-quiz-result-fields');
    const fieldsRowW = fieldsRow?.clientWidth || 0;

    const measureNatural = (svg) => {
      const textEl = svg.querySelector('text.dl-psq-quiz-result-value-text');
      if (!textEl) return null;

      textEl.setAttribute('x', '0');
      textEl.setAttribute('y', '50%');
      textEl.setAttribute('dominant-baseline', 'central');

      let bbox;
      try {
        bbox = textEl.getBBox();
      } catch (e) {
        return null;
      }
      if (!bbox || !Number.isFinite(bbox.width) || bbox.width <= 0) return null;

      textEl.setAttribute('x', String(-bbox.x));

      let tight;
      try {
        tight = textEl.getBBox();
      } catch (e) {
        return null;
      }
      if (!tight || !Number.isFinite(tight.width) || tight.width <= 0) return null;

      const fontSize =
        parseFloat(getComputedStyle(svg).fontSize) ||
        parseFloat(getComputedStyle(textEl).fontSize) ||
        40;
      const lineH = fontSize * 1.4;
      const w = Math.max(1, tight.width);

      svg.setAttribute('viewBox', `0 0 ${w} ${lineH}`);
      svg.setAttribute('preserveAspectRatio', 'xMinYMid meet');
      textEl.setAttribute('y', String(lineH / 2));

      return { svg, textEl, w, lineH };
    };

    const applyScale = (measured, scale) => {
      if (!measured) return;
      const s = Math.max(0.01, Math.min(1, scale));
      measured.svg.style.width = `${measured.w * s}px`;
      measured.svg.style.height = `${measured.lineH * s}px`;
    };

    const nameSvg = svgs.find((s) => s.closest('.dl-psq-quiz-result-field--name'));
    const typeSvg = svgs.find((s) => s.closest('.dl-psq-quiz-result-field--type'));
    const otherSvgs = svgs.filter((s) => s !== nameSvg && s !== typeSvg);

    const nameM = nameSvg ? measureNatural(nameSvg) : null;
    const typeM = typeSvg ? measureNatural(typeSvg) : null;

    // Mobile two-column: keep both SVGs at the same visual size.
    // Name column grows with content up to 50%; type gets the rest.
    // Scale both down together when they don't fit side-by-side.
    const isMobilePair =
      nameM &&
      typeM &&
      fieldsRowW > 0 &&
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 990px)').matches;

    if (isMobilePair) {
      const nameField = nameSvg.closest('.dl-psq-quiz-result-field--name');
      const typeField = typeSvg.closest('.dl-psq-quiz-result-field--type');
      const namePad =
        (parseFloat(getComputedStyle(nameField).paddingRight) || 0) +
        (parseFloat(getComputedStyle(nameField).paddingLeft) || 0);
      const typePad =
        (parseFloat(getComputedStyle(typeField).paddingRight) || 0) +
        (parseFloat(getComputedStyle(typeField).paddingLeft) || 0);
      // Vertical divider ~1px
      const usable = Math.max(1, fieldsRowW - namePad - typePad - 1);
      const half = usable * 0.5;

      let scale = 1;
      const sideBySide = usable / (nameM.w + typeM.w);
      // If at side-by-side scale name still fits in half, use it; otherwise both share half/half
      if (nameM.w * Math.min(1, sideBySide) <= half) {
        scale = Math.min(1, sideBySide);
      } else {
        scale = Math.min(1, half / nameM.w, half / typeM.w);
      }
      scale = Math.max(0.01, scale);

      applyScale(nameM, scale);
      applyScale(typeM, scale);
    } else {
      // Desktop / single field: natural size, shrink only to own parent if needed
      [nameM, typeM].forEach((m) => {
        if (!m) return;
        const parentW = m.svg.parentElement?.clientWidth || 0;
        const scale = parentW > 0 && m.w > parentW ? parentW / m.w : 1;
        applyScale(m, scale);
      });
    }

    otherSvgs.forEach((svg) => {
      const m = measureNatural(svg);
      if (!m) return;
      const parentW = svg.parentElement?.clientWidth || 0;
      const scale = parentW > 0 && m.w > parentW ? parentW / m.w : 1;
      applyScale(m, scale);
    });
  }

  updateActions() {
    const panel = this.quizPanel;
    const setResultStepUI = (on) => {
      if (!panel) return;
      panel.classList.toggle('is-result-step', Boolean(on));
    };

    if (!this.quizFlow || this.quizFlow.hidden) {
      setResultStepUI(false);
      if (this.btnBack) this.btnBack.hidden = true;
      if (this.btnNext) this.btnNext.hidden = true;
      if (this.btnFinish) this.btnFinish.hidden = true;
      if (this.btnCancel) this.btnCancel.hidden = true;
      return;
    }

    const isInfo = this.currentStepIndex === 0;
    const isSubscribeGate = this.currentStepIndex === this.getSubscribeStepIndex();
    const isResult = this.currentStepIndex === this.getResultStepIndex();

    setResultStepUI(isResult || isSubscribeGate);

    if (this.progressEl) {
      this.progressEl.hidden = true;
    }

    if (this.btnBack) this.btnBack.hidden = isInfo || isResult || isSubscribeGate;
    // Questions auto-advance on option click — Next only for info step; Finish unused
    if (this.btnNext) this.btnNext.hidden = !isInfo || isResult || isSubscribeGate;
    if (this.btnFinish) this.btnFinish.hidden = true;
    if (this.btnCancel) this.btnCancel.hidden = !isInfo || isResult || isSubscribeGate;
  }

  handleResultSubscribe(form) {
    if (this.isSubscribing) return;
    if (!form || form.dataset.psqMode === 'restart') return;
    const emailInput = form?.querySelector?.('[name="contact[email]"]');
    const email = (emailInput?.value || '').trim();
    const invalidEmailTip = this.strings.invalidEmailTip || 'Please enter a valid email address.';
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      this.showInlineMessage(invalidEmailTip);
      return;
    }

    this.clearInlineMessage();

    const button = form.querySelector('.dl-psq-quiz-result-subscribe-btn');
    this.isSubscribing = true;
    if (button) {
      button.disabled = true;
      button.classList.add('is-disabled');
      button.setAttribute('aria-disabled', 'true');
    }
    if (emailInput) emailInput.disabled = true;

    let submittedOk = false;
    try {
      const ok = this.syncSubscribeToBraze(email) !== false;
      submittedOk = ok;

      const payload = this.buildTrackingPayload({ stage: 'subscribe', email, result_key: this.finalResultKey });
      console.log("payload",payload);
      if (this.events.subscribe) window.DLPixelH?.capture?.(this.events.subscribe, payload);

      const successText = form.getAttribute('data-success-text') || PSQ_MSG.quizResultSuccess;
      const errorText = form.getAttribute('data-error-text') || PSQ_MSG.quizResultError;
      this.showFormMessage(form, ok ? 'success' : 'error', ok ? successText : errorText);
      this.scrollQuizInternalTo(form.querySelector('.dl-psq-subscription-message'));

      if (ok) {
        this.saveSubscribedEmail(email);
        if (emailInput) emailInput.value = email;
        this.setResultSubscribeRestartUI(form);
      }
    } catch (e) {
      const errorText = form.getAttribute('data-error-text') || PSQ_MSG.quizResultError;
      this.showFormMessage(form, 'error', errorText);
      this.scrollQuizInternalTo(form.querySelector('.dl-psq-subscription-message'));
    } finally {
      if (!submittedOk) {
        if (button) {
          button.disabled = false;
          button.classList.remove('is-disabled');
          button.setAttribute('aria-disabled', 'false');
        }
        if (emailInput) emailInput.disabled = false;
      }
      this.isSubscribing = false;
    }
  }

  syncSubscribeToBraze(email) {
    const petName =
      (this.infoAnswers?.pet_name || this.infoAnswers?.petName || this.infoAnswers?.name || '').toString().trim();
    const petMbti = (this.matchedMBTI?.mbti_type || this.finalResultKey || '').toString().trim();
    const answerString = (this.answerString || '').toString().trim();

    let didSend = false;

    // Prefer unified pixel entry: keep behavior consistent across sites (see dl-page-trade-in-h.js).
    try {
      if (typeof DLPixelH !== 'undefined') {
        if (typeof DLPixelH.identify === 'function') {
          DLPixelH.identify(email);
          didSend = true;
        }

        // In dl-page-trade-in-h.js:
        // - addToUserArrayProperty(): append values to an *array* attribute (used for accumulating many entries)
        // - setUserProperty(): set/overwrite a *scalar* attribute (used for single-value fields like timestamps)
        //
        // For quiz subscription we want *scalar* custom attributes, so use setUserProperty only.
        if (typeof DLPixelH.setUserProperty === 'function') {
          if (petName) DLPixelH.setUserProperty('pet_name', petName);
          if (petMbti) DLPixelH.setUserProperty('pet_mbti', petMbti);
          if (answerString) DLPixelH.setUserProperty('mbti_quiz_answers', answerString);
          didSend = true;
        }

        if (didSend) return didSend;
      }
    } catch (e) {
      // ignore
    }

    // Fallback: direct Braze SDK (best-effort) if DLPixelH is not available.
    const brazeApi = window.braze;
    if (!brazeApi?.getUser) return didSend;

    try {
      if (typeof brazeApi.changeUser === 'function') brazeApi.changeUser(email);
      const user = brazeApi.getUser();
      if (!user) return;

      if (typeof user.setEmail === 'function') user.setEmail(email);
      if (typeof user.addAlias === 'function') user.addAlias(email, 'user_email');
      if (typeof user.setEmailNotificationSubscriptionType === 'function') {
        user.setEmailNotificationSubscriptionType('opted_in');
      }

      if (typeof user.setCustomUserAttribute === 'function') {
        if (petName) user.setCustomUserAttribute('pet_name', petName);
        if (petMbti) user.setCustomUserAttribute('pet_mbti', petMbti);
        if (answerString) user.setCustomUserAttribute('mbti_quiz_answers', answerString);
      }

      if (typeof brazeApi.requestImmediateDataFlush === 'function') brazeApi.requestImmediateDataFlush();
      didSend = true;
    } catch (e) {
      // ignore
    }

    return didSend;
  }

  escapeHTML(str) {
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  escapeAttr(str) {
    return this.escapeHTML(str).replaceAll('`', '&#96;');
  }
}

if (!customElements.get('dl-page-subscription-quiz-m')) {
  customElements.define('dl-page-subscription-quiz-m', DlPageSubscriptionQuizM);
}